// Presentational building blocks for the operations-feed dashboard.
// Deliberately dumb — no fetching, no permissions, no routing decisions.
// Consumed by pages/app/Home.tsx (live data) and the design preview
// (stub data), same pattern as elements/Incident.
import { ReactNode } from "react";
import { Link } from "react-router-dom";
import moment from "moment";

// ── panel chrome ──────────────────────────────────────────────────────────

export function FwPanel({
  title,
  action,
  children,
}: {
  title: string;
  action?: { label: string; to: string };
  children: ReactNode;
}) {
  return (
    <section className="fw-panel">
      <div className="fw-panel__head">
        <span className="fw-panel__title">{title}</span>
        {action && <Link to={action.to}>{action.label}</Link>}
      </div>
      {children}
    </section>
  );
}

export function FwEmpty({ children }: { children: ReactNode }) {
  return <div className="fw-panel__empty">{children}</div>;
}

// ── stat strip ────────────────────────────────────────────────────────────

export type StatItem = {
  label: string;
  value: number | string;
  /** Optional module link; omitted when the viewer lacks permission. */
  to?: string;
};

export function StatStrip({ items }: { items: StatItem[] }) {
  return (
    <div className="fw-panel">
      <dl className="fw-statstrip" style={{ margin: 0 }}>
        {items.map((s) =>
          s.to ? (
            <Link key={s.label} to={s.to} className="fw-statstrip__item">
              <dt>{s.label}</dt>
              <dd>{formatCount(s.value)}</dd>
            </Link>
          ) : (
            <div key={s.label} className="fw-statstrip__item">
              <dt>{s.label}</dt>
              <dd>{formatCount(s.value)}</dd>
            </div>
          )
        )}
      </dl>
    </div>
  );
}

function formatCount(v: number | string) {
  const n = typeof v === "number" ? v : parseInt(v, 10);
  if (isNaN(n)) return v;
  return n.toLocaleString();
}

// ── incident activity feed ────────────────────────────────────────────────

export type FeedIncident = {
  _id: string;
  field1Value?: string;
  locality?: { name?: string };
  subLocality?: { name?: string };
  address?: string;
  createdBy?: { firstName?: string; lastName?: string };
  modifiedBy?: { firstName?: string; lastName?: string };
  versionNumber?: number;
  createdAt?: string;
  updatedAt?: string;
  sendPushNotification?: boolean;
  featured?: boolean;
  featuredImageUrl?: string | null;
};

export function personName(p?: { firstName?: string; lastName?: string }) {
  if (!p?.firstName && !p?.lastName) return "—";
  return [p?.firstName, p?.lastName].filter(Boolean).join(" ");
}

export function timeAgo(iso?: string) {
  return iso ? moment(iso).fromNow() : "—";
}

export function ActivityRow({ item }: { item: FeedIncident }) {
  const isUpdate = (item.versionNumber || 0) >= 1;
  const when = item.updatedAt || item.createdAt;
  const who = isUpdate
    ? personName(item.modifiedBy || item.createdBy)
    : personName(item.createdBy);

  return (
    <Link to={`/incident/form?id=${item._id}&mode=view`} className="fw-feedrow">
      <div className="fw-feedrow__main">
        <div className="fw-feedrow__title">
          <span
            className={`fw-badge ${isUpdate ? "fw-badge--info" : "fw-badge--danger"}`}
          >
            {isUpdate ? `v${item.versionNumber}` : "New"}
          </span>
          <span>{item.field1Value || "Incident"}</span>
          {item.locality?.name && (
            <span className="fw-feedrow__locality">· {item.locality.name}</span>
          )}
        </div>
        <div className="fw-feedrow__meta">
          {item.address && (
            <span className="fw-feedrow__address">{item.address}</span>
          )}
          <span>· {who}</span>
        </div>
      </div>
      <div className="fw-feedrow__side fw-num">
        {item.sendPushNotification && (
          <i className="ri-notification-3-line" title="Push sent" />
        )}
        {item.featured && <i className="ri-star-line" title="Featured" />}
        <span>{timeAgo(when)}</span>
      </div>
    </Link>
  );
}

// ── needs attention ───────────────────────────────────────────────────────
// Derived CLIENT-SIDE from the same incident list — the API has no
// dedicated "attention" endpoint (logged as an API wish).

export type AttentionItem = {
  id: string;
  to: string;
  title: string;
  reason: string;
};

export function deriveAttention(items: FeedIncident[]): AttentionItem[] {
  const out: AttentionItem[] = [];
  for (const it of items) {
    const label = [it.field1Value || "Incident", it.locality?.name]
      .filter(Boolean)
      .join(" · ");
    if (it.featured && !it.featuredImageUrl) {
      out.push({
        id: `${it._id}-img`,
        to: `/incident/form?id=${it._id}&mode=view`,
        title: label,
        reason: "Featured, but has no featured image",
      });
    }
    if (it.sendPushNotification === false) {
      out.push({
        id: `${it._id}-push`,
        to: `/incident/form?id=${it._id}&mode=view`,
        title: label,
        reason: "Posted without a push notification",
      });
    }
  }
  return out.slice(0, 6);
}

export function AttentionRow({ item }: { item: AttentionItem }) {
  return (
    <Link to={item.to} className="fw-attn">
      <i className="ri-error-warning-line" />
      <span className="fw-attn__text">
        <strong>{item.title}</strong>
        <span>{item.reason}</span>
      </span>
    </Link>
  );
}

// ── compact user rows ─────────────────────────────────────────────────────

export type FeedUser = {
  _id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  createdAt?: string;
};

export function UserRow({ item }: { item: FeedUser }) {
  const initials =
    (item.firstName?.[0] || "") + (item.lastName?.[0] || "") ||
    item.email?.[0] ||
    "?";
  return (
    <div className="fw-userrow">
      <span className="fw-userrow__avatar">{initials}</span>
      <span className="fw-userrow__main">
        <strong>{personName(item) === "—" ? "user" : personName(item)}</strong>
        <span>{item.email || "—"}</span>
      </span>
      <span className="fw-userrow__time">{timeAgo(item.createdAt)}</span>
    </div>
  );
}
